/**
 * <frequently-bought-together-loader>
 * Loads recommendations, recalculates bundle totals instantly, and
 * submits through CartManager so cart UI stays synchronized.
 */
(function () {
  if (customElements.get('frequently-bought-together-loader')) return;

  class FrequentlyBoughtTogetherLoader extends HTMLElement {
    connectedCallback() {
      this.slot = this.querySelector('[data-fbt-slot]');
      this.summary = this.querySelector('[data-fbt-summary]');
      this.totalEl = this.querySelector('[data-fbt-total-price]');
      this.originalEl = this.querySelector('[data-fbt-original-price]');
      this.savingsEl = this.querySelector('[data-fbt-savings]');
      this.countEl = this.querySelector('[data-fbt-count]');
      this.addAllBtn = this.querySelector('[data-fbt-add-all]');
      this.mainVariantId = Number(this.dataset.mainVariantId);
      this.mainPrice = Number(this.dataset.mainPrice);
      this.discountPercent = Number(this.dataset.bundleDiscountPercent || 10);
      this._isAdding = false;
      this._onChange = (event) => {
        if (event.target.matches('.fbt-card__checkbox')) this.recalculate();
      };

      this.addAllBtn?.addEventListener('click', () => this.addAll());
      this.load();
    }

    disconnectedCallback() {
      this.slot?.removeEventListener('change', this._onChange);
    }

    async load() {
      try {
        const response = await fetch(this.dataset.url);
        if (!response.ok) throw new Error('FBT fetch failed');

        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fragment = doc.querySelector('.fbt');

        if (!fragment) {
          this.remove();
          return;
        }

        this.slot.innerHTML = '';
        this.slot.appendChild(fragment);
        this.summary.hidden = false;
        this.slot.addEventListener('change', this._onChange);
        this.recalculate();
      } catch (error) {
        console.error('[FBT] could not load recommendations', error);
        this.remove();
      }
    }

    getCheckedItems() {
      return Array.from(this.slot.querySelectorAll('.fbt-card__checkbox:checked'));
    }

    recalculate() {
      const checked = this.getCheckedItems();
      const addOnsTotal = checked.reduce((sum, element) => sum + Number(element.dataset.price || 0), 0);
      const originalTotal = this.mainPrice + addOnsTotal;
      const eligibleForSavings = checked.length > 0;
      const savings = eligibleForSavings ? Math.round((originalTotal * this.discountPercent) / 100) : 0;
      const bundleTotal = Math.max(originalTotal - savings, 0);
      const itemCount = checked.length + 1;
      const fmt = (cents) => window.RewardManager?.formatMoney(cents) || window.LuxuryCartUtils?.formatMoney(cents);

      if (this.originalEl) this.originalEl.textContent = fmt(originalTotal);
      if (this.totalEl) this.totalEl.textContent = fmt(bundleTotal);
      if (this.savingsEl) {
        this.savingsEl.textContent = savings > 0 ? `Save ${fmt(savings)}` : 'Select an item to save';
        this.savingsEl.classList.toggle('is-active', savings > 0);
      }
      if (this.countEl) this.countEl.textContent = `${itemCount} item${itemCount > 1 ? 's' : ''}`;
      if (this.addAllBtn) this.addAllBtn.disabled = this._isAdding || !this.mainVariantId;
    }

    async addAll() {
      if (this._isAdding) return;
      this._isAdding = true;
      this.setLoading(true);

      const bundleProperty = { _bundle: 'Frequently Bought Together' };
      const items = [{ id: this.mainVariantId, quantity: 1, properties: bundleProperty }];
      this.getCheckedItems().forEach((element) => {
        items.push({
          id: Number(element.dataset.variantId),
          quantity: 1,
          properties: bundleProperty,
        });
      });

      try {
        await window.CartManager.addItems(items);
        window.LuxuryAnimations?.pulse(this.summary, 'fbt-summary--added');
      } catch (error) {
        console.error('[FBT] add all failed', error);
      } finally {
        this._isAdding = false;
        this.setLoading(false);
      }
    }

    setLoading(isLoading) {
      if (!this.addAllBtn) return;
      this.addAllBtn.disabled = isLoading;
      this.addAllBtn.classList.toggle('is-loading', isLoading);
      this.addAllBtn.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    }
  }

  customElements.define('frequently-bought-together-loader', FrequentlyBoughtTogetherLoader);
})();
